import React from 'react';
import classNames from 'classnames';

import { IconCard } from '../../components';

import { getBlogCategory } from './blogArticles';
import { GENERAL_IMAGE_KEY } from './BlogPage.duck';

import css from './BlogPage.module.css';

/**
 * Article visual: a photo from a Formabel listing of the category when there is one,
 * otherwise a gradient in the category colors with its icon.
 *
 * @component
 * @param {Object} props
 * @param {string} props.className visual size and shape
 * @param {string|null} props.categoryId Console id of the category
 * @param {Object?} props.image listing photo variants { medium, large }
 * @param {string?} props.alt photo description (for Google Images and screen readers)
 * @param {string?} props.sizes img sizes attribute
 * @param {boolean?} props.showLabel show the category name in the corner
 * @returns {JSX.Element}
 */
export const BlogVisual = props => {
  const { className, categoryId, image, alt = '', sizes = '100vw', showLabel = false } = props;
  const category = getBlogCategory(categoryId);
  const [from, to] = category.colors;
  const { medium, large } = image || {};

  return (
    <div
      className={classNames(css.visual, className)}
      style={{ backgroundImage: `linear-gradient(135deg, ${from}, ${to})` }}
    >
      {large ? (
        <img
          className={css.visualPhoto}
          src={large.url}
          srcSet={[medium, large]
            .filter(Boolean)
            .map(v => `${v.url} ${v.width}w`)
            .join(', ')}
          sizes={sizes}
          alt={alt}
          loading="lazy"
        />
      ) : (
        <span className={css.visualIcon} aria-hidden="true">
          <IconCard brand={categoryId || 'others'} />
        </span>
      )}
      {showLabel ? <span className={css.visualLabel}>{category.label}</span> : null}
    </div>
  );
};

/**
 * Render article text where **double asterisks** mark bold words.
 *
 * @component
 * @param {Object} props
 * @param {string} props.text
 * @returns {JSX.Element}
 */
export const BlogText = props => {
  const parts = props.text.split('**');
  return <>{parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))}</>;
};

/**
 * Remove the bold markers, for meta tags and structured data.
 *
 * @param {string} text
 * @returns {string}
 */
export const toPlainText = text => text.replace(/\*\*/g, '');

/**
 * Listing photo for an article card: a photo of its category, or of any category for general
 * articles. Different `index` values pick different photos when the category has several.
 *
 * @param {Object} categoryImages lists of photos by category id (BlogPage state)
 * @param {string|null} categoryId
 * @param {number?} index position of the article among the articles of its category
 * @returns {Object|null} photo variants { medium, large }
 */
export const getCategoryImage = (categoryImages, categoryId, index = 0) => {
  const images = (categoryImages || {})[categoryId || GENERAL_IMAGE_KEY] || [];
  return images.length > 0 ? images[index % images.length] : null;
};

/**
 * Photo of the first listing that has one, in the same shape as the category photos.
 *
 * @param {Array<Object>} listings listing entities with images
 * @returns {Object|null} photo variants { medium, large }
 */
export const getFirstListingImage = listings => {
  const variants = listings
    .map(l => l.images?.[0]?.attributes?.variants)
    .find(v => v?.['scaled-large']);
  return variants ? { medium: variants['scaled-medium'], large: variants['scaled-large'] } : null;
};

/**
 * Photo for an article: its own illustration, else the illustration of its category (files in
 * public/static/blog), else a listing photo of the category.
 *
 * @param {Object} article blog article
 * @param {Object} categoryImages lists of listing photos by category id (BlogPage state)
 * @param {number?} index position of the article among the articles of its category
 * @returns {Object|null} photo variants { medium, large }
 */
export const getArticleImage = (article, categoryImages, index = 0) => {
  const illustration = article.image || getBlogCategory(article.category).image;
  return illustration
    ? { large: { url: illustration, width: 1600 } }
    : getCategoryImage(categoryImages, article.category, index);
};
